import { useMutation, useQueryClient } from "@tanstack/react-query";

import useApiHelper from "../hooks/useApiHelper";
import {
  ICreateWorkOrderRequest,
  ISingleWorkOrderResponse,
  IUpdateWorkOrderItemPriceRequest,
  IUpdateWorkOrderItemRejectionRequest,
  IUpdateWorkOrderStatusRequest,
} from "../models/WorkOrder";
import { DefaultResponse, DefaultResponseWithData } from "../models/system";

export default function useWorkOrderMutations() {
  const { post, put, patch, del } = useApiHelper();
  const queryClient = useQueryClient();

  const invalidateAll = (workOrderId?: string) => {
    queryClient.invalidateQueries(["workOrders"]);
    queryClient.invalidateQueries(["pagedTable", "/workorder/paginated"]);
    queryClient.invalidateQueries(["workOrderStats"]);
    queryClient.invalidateQueries(["workOrderNotifications"]);
    if (workOrderId) {
      queryClient.invalidateQueries(["workOrder", workOrderId]);
      queryClient.invalidateQueries(["workOrderParts", workOrderId]);
    }
  };

  const create = useMutation(
    (request: ICreateWorkOrderRequest) =>
      post<ICreateWorkOrderRequest, ISingleWorkOrderResponse>("/workorder", request),
    {
      onSuccess: () => invalidateAll(),
    }
  );

  const update = useMutation(
    ({ workOrderId, request }: { workOrderId: string; request: ICreateWorkOrderRequest }) =>
      put<ICreateWorkOrderRequest, ISingleWorkOrderResponse>(`/workorder/${workOrderId}`, request),
    {
      onSuccess: (_data, variables) => invalidateAll(variables.workOrderId),
    }
  );

  const updateStatus = useMutation(
    ({ workOrderId, status }: { workOrderId: string; status: IUpdateWorkOrderStatusRequest }) =>
      patch<ISingleWorkOrderResponse>(`/workorder/${workOrderId}/status`, status),
    {
      onSuccess: (_data, variables) => invalidateAll(variables.workOrderId),
    }
  );

  const remove = useMutation(
    (workOrderId: string) => del<DefaultResponse>(`/workorder/${workOrderId}`),
    {
      onSuccess: () => invalidateAll(),
    }
  );

  const updateItemRejection = useMutation(
    ({ workOrderId, itemId, body }: { workOrderId: string; itemId: string; body: IUpdateWorkOrderItemRejectionRequest }) =>
      patch<DefaultResponseWithData<any>>(`/workorder/${workOrderId}/items/${itemId}/rejection`, body),
    {
      onSuccess: (_data, variables) => invalidateAll(variables.workOrderId),
    }
  );

  const updateItemPrice = useMutation(
    ({ workOrderId, itemId, body }: { workOrderId: string; itemId: string; body: IUpdateWorkOrderItemPriceRequest }) =>
      patch<DefaultResponseWithData<any>>(`/workorder/${workOrderId}/items/${itemId}/price`, body),
    {
      onSuccess: (_data, variables) => invalidateAll(variables.workOrderId),
    }
  );

  const removeItem = useMutation(
    ({ workOrderId, itemId }: { workOrderId: string; itemId: string }) =>
      del<DefaultResponse>(`/workorder/${workOrderId}/items/${itemId}`),
    {
      onSuccess: (_data, variables) => invalidateAll(variables.workOrderId),
    }
  );

  return {
    create,
    update,
    updateStatus,
    remove,
    updateItemRejection,
    updateItemPrice,
    removeItem,
  };
}
